/**
 * useQuoteForm - Presentation Hook
 *
 * Encapsulates form state management for quote response
 * Manages items, notes, and files state
 *
 * @module useQuoteForm
 */

import { useState, useCallback } from "react";
import type { QuoteItem } from "@semo/shared-utils";

export interface QuoteFormFile {
  id: string;
  file?: File;
  fileName: string;
  fileSize: string;
  url?: string;
  filePath?: string;
  isExisting?: boolean;
}

export interface UseQuoteFormProps {
  initialItems?: QuoteItem[];
  initialNotes?: string;
  initialFiles?: QuoteFormFile[];
}

/**
 * Custom hook for quote form state management
 * @param props - Initial values for items and notes
 * @returns Form state and manipulation functions
 */
export const useQuoteForm = (props?: UseQuoteFormProps) => {
  const [items, setItems] = useState<QuoteItem[]>(
    props?.initialItems || [createEmptyItem()],
  );
  const [notes, setNotes] = useState(props?.initialNotes || "");
  const [files, setFiles] = useState<QuoteFormFile[]>(
    props?.initialFiles || [],
  );

  // Add new empty item
  const addItem = useCallback(() => {
    setItems((prev) => [...prev, createEmptyItem()]);
  }, []);

  // Remove item at index
  const removeItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Update specific field of item at index
  const updateItem = useCallback(
    <K extends keyof QuoteItem>(
      index: number,
      field: K,
      value: QuoteItem[K],
    ) => {
      setItems((prev) => {
        return prev.map((item, i) => {
          if (i !== index) return item;
          return { ...item, [field]: value };
        });
      });
    },
    [],
  );

  // Add file
  const addFile = useCallback((file: File) => {
    const newFile: QuoteFormFile = {
      id: `${Date.now()}-${Math.random()}`,
      file,
      fileName: file.name,
      fileSize: formatFileSize(file.size),
      isExisting: false,
    };
    setFiles((prev) => [...prev, newFile]);
  }, []);

  // Remove file by ID
  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  // Clear all files
  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  // Reset entire form
  const resetForm = useCallback(
    (newItems?: QuoteItem[], newNotes?: string, newFiles?: QuoteFormFile[]) => {
      setItems(newItems || [createEmptyItem()]);
      setNotes(newNotes || "");
      setFiles(newFiles || []);
    },
    [],
  );

  return {
    // State
    items,
    notes,
    files,

    // Item operations
    addItem,
    removeItem,
    updateItem,

    // File operations
    addFile,
    removeFile,
    clearFiles,

    // Notes operations
    setNotes,

    // Form operations
    resetForm,
  };
};

/**
 * Create an empty quote item
 */
function createEmptyItem(): QuoteItem {
  return {
    itemName: "",
    quantity: 1,
    unitPrice: 0,
    unit: "개",
  };
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}
